import React, { useEffect } from 'react';

const isElementOverflowing = (element: HTMLElement) =>
  element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;

export const useDetectTextOverflow = (selector?: string) => {
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = React.useState(false);

  useEffect(() => {
    const root = textRef.current;
    if (!root) {
      return undefined;
    }

    const getTarget = () => {
      if (!selector) {
        return root;
      }

      return root.querySelector<HTMLElement>(selector) ?? root;
    };

    const target = getTarget();
    if (!target) {
      setIsOverflow(false);
      return undefined;
    }

    const checkOverflow = () => {
      setIsOverflow(isElementOverflowing(target));
    };

    checkOverflow();

    const observers: Array<{ disconnect: () => void }> = [];

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(root);
      if (target !== root) {
        resizeObserver.observe(target);
      }
      observers.push(resizeObserver);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkOverflow);
      observers.push({ disconnect: () => window.removeEventListener('resize', checkOverflow) });
    }

    if (typeof MutationObserver !== 'undefined') {
      const mutationObserver = new MutationObserver(checkOverflow);
      mutationObserver.observe(target, {
        childList: true,
        characterData: true,
        subtree: true,
      });
      observers.push(mutationObserver);
    }

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [selector]);

  return {
    isOverflow,
    textRef,
  };
};
